import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircle, Check } from 'lucide-react';
import useWhatsApp from '@/lib/useWhatsApp';
import { useLang } from '@/lib/LanguageContext';

// Approximate starting price (USD) and pricing-page section per service.
const PRICE_MAP = {
  'website-development-dubai': [1499, 'websites'],
  'landing-page-design-dubai': [1499, 'websites'],
  'saas-development-dubai': [9999, 'custom-tech'],
  'mobile-app-development-dubai': [4999, 'custom-tech'],
  'game-development-uae': [24999, 'custom-tech'],
  'custom-ai-solutions-dubai': [2999, 'custom-tech'],
  'business-automation-dubai': [399, 'addons'],
  'digital-marketing-dubai': [499, 'addons'],
  'cybersecurity-ai-uae': [50000, 'ai-web']
};

export default function ServiceRow({ service, index }) {
  const { waLink, t } = useWhatsApp();
  const es = useLang().language === 'es';
  const Icon = service.icon;
  const reversed = index % 2 === 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7 }}
      className="grid md:grid-cols-2 gap-10 items-center py-12 border-b border-border/30 last:border-0">
      <div className={reversed ? 'md:order-2' : ''}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <span className="text-xs font-inter font-medium tracking-[0.2em] uppercase text-primary">
            {t(service.tag)}
          </span>
        </div>
        <h2 className="font-inter font-bold text-2xl md:text-3xl text-foreground mb-4">
          <Link to={`/services/${service.slug}`} className="hover:text-primary transition-colors">{t(service.title)}</Link>
        </h2>
        <p className="text-sm text-muted-foreground font-inter leading-relaxed mb-6">
          {t(service.description)}
        </p>
        {PRICE_MAP[service.slug] && (
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="text-sm text-muted-foreground font-inter">
              {es ? 'Desde aprox.' : 'From approx.'}{' '}
              <span className="font-black text-xl text-primary">${PRICE_MAP[service.slug][0].toLocaleString('en-US')} USD</span>
            </span>
            <Link to={`/prices#${PRICE_MAP[service.slug][1]}`} className="text-xs font-inter font-semibold tracking-wider uppercase text-primary hover:underline">
              {es ? 'Ver más →' : 'See more →'}
            </Link>
          </div>
        )}
        <a
          href={waLink(service.waMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-inter font-semibold text-xs tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all duration-300">
          <MessageCircle className="w-4 h-4" />
          {t('Request This Service')}
        </a>
      </div>
      <div className={reversed ? 'md:order-1' : ''}>
        <div className="rounded-2xl border border-border/30 bg-card/30 backdrop-blur-sm p-6">
          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground font-inter">{t(feature)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}